import { api } from '@/lib/api';
import {
  InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import {
  AddOptOutFileParams,
  AddOptOutParams,
  AddOptOutResponse,
  DeleteOptOutParams,
  DeleteOptOutResponse,
  FetchOptOutsParams,
  OptOutItem,
  OptOutListResponse,
} from './types';

const _fetchOptOuts = async (
  params: FetchOptOutsParams
): Promise<AxiosResponse<OptOutListResponse>> => {
  const searchParams = new URLSearchParams();
  searchParams.append('account_id', params.account_id);

  if (params.identity_id) searchParams.append('identity_id', params.identity_id);
  if (params.page) searchParams.append('page', params.page.toString());
  if (params.per_page) searchParams.append('per_page', params.per_page.toString());
  if (params.dnc_type) searchParams.append('dnc_type', params.dnc_type);
  if (params.query) searchParams.append('query', params.query);

  return api.get(`dnc/listByAccount?${searchParams.toString()}`);
};

const _deleteOptOut = async (
  params: DeleteOptOutParams
): Promise<DeleteOptOutResponse[]> => {
  const response = await api.delete(
    `dnc/${params.value}?account_id=${params.account_id}`
  );
  return response.data;
};

const _addOptOut = async (params: AddOptOutParams): Promise<AddOptOutResponse> => {
  const response = await api.post('dnc', params);
  return response.data;
};

const _addOptOutFile = async (
  params: AddOptOutFileParams
): Promise<{ message: string }> => {
  const formData = new FormData();
  formData.append('account_id', params.account_id);
  if (params.identity_id) {
    formData.append('identity_id', params.identity_id);
  }
  formData.append('file', params.file);
  formData.append('reason', params.reason);

  const response = await api.post('dnc/uploadlist', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const useFetchOptOuts = (params: FetchOptOutsParams, enabled: boolean = true) => {
  return useInfiniteQuery<
    AxiosResponse<OptOutListResponse>,
    Error,
    AxiosResponse<OptOutListResponse>,
    (string | FetchOptOutsParams)[],
    number
  >({
    queryKey: ['optOuts', params],
    queryFn: ({ pageParam = 1 }) => _fetchOptOuts({ ...params, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { pagination } = lastPage.data;
      return pagination.page < pagination.totalPages ? pagination.page + 1 : undefined;
    },
    enabled,
  });
};

export const useDeleteOptOut = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: _deleteOptOut,
    onSuccess: () => {
      // Invalidate queries to refresh the table
      queryClient.invalidateQueries({ queryKey: ['optOuts'] });
    },
  });
};

export const useAddOptOut = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: _addOptOut,
    onMutate: async (params) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: ['optOuts'] });

      // Snapshot the previous value
      const previousData = queryClient.getQueriesData({ queryKey: ['optOuts'] });

      // Create optimistic item for immediate UI update
      const optimisticItem: OptOutItem = {
        dnc_group: 'main',
        sync_state: 1,
        added_date: new Date().toISOString().replace('T', ' ').substring(0, 19),
        value: params.value,
        added_by: 'Current User', // Will be updated from response
        dnc_type: params.dnc_type,
        reason: params.reason,
      };

      // Optimistically update the cache
      queryClient.setQueriesData(
        { queryKey: ['optOuts'] },
        (old: InfiniteData<AxiosResponse<OptOutListResponse>> | undefined) => {
          if (!old) return old;

          return {
            ...old,
            pages: old.pages.map(
              (page: AxiosResponse<OptOutListResponse>, index: number) => {
                // Add to the first page
                if (index === 0) {
                  return {
                    ...page,
                    data: {
                      ...page.data,
                      items: [optimisticItem, ...page.data.items],
                    },
                  };
                }
                return page;
              }
            ),
          };
        }
      );

      return { previousData, optimisticItem };
    },
    onError: (error, params, context) => {
      // Revert optimistic update on error
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSuccess: () => {
      // Invalidate opt-outs queries to refresh with real data
      queryClient.invalidateQueries({ queryKey: ['optOuts'] });
    },
  });
};

export const useAddOptOutFile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: _addOptOutFile,
    onSuccess: () => {
      // File uploads will refresh the entire list after success
      // since we don't know how many items will be added
      queryClient.invalidateQueries({ queryKey: ['optOuts'] });
    },
  });
};
